import { Fragment, useCallback, useEffect, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import { Grid, TextField } from '@material-ui/core';
import { ConfirmButton, CancelButton, useStyles } from './ExaminerAssignNumber.styles';
import { SelectedExaminer } from '../../../services/examiners/examiners.types';

interface ExaminerAssignNumberProps {
  open: boolean;
  examiners: SelectedExaminer[];
  confirm: (data: SelectedExaminer[]) => void;
  cancel: () => void;
  isBold?: boolean;
  closeDialogs?: () => void;
  isEdit?: boolean;
}

export default function ExaminerAssignNumber(props: ExaminerAssignNumberProps): JSX.Element {
  const classes = useStyles();

  const [open, setOpen] = useState<boolean>(false);
  const [examinerNumbers, setExaminerNumbers] = useState<SelectedExaminer[]>([]);
  const [isAddButtonDisable, setIsAddButtonDisable] = useState<boolean>(props.isEdit ?? false);
  const [selectedExaminer, setSelectedExaminer] = useState<SelectedExaminer>();
  const cloneExaminer = useCallback((examiner: SelectedExaminer): SelectedExaminer => ({ ...examiner }), []);

  useEffect(() => {
    setOpen(props.open);
  }, [props.open]);

  useEffect(() => {
    if (props.examiners) {
      setExaminerNumbers(props.examiners.map(cloneExaminer));
    }
  }, [cloneExaminer, props.examiners, props.isEdit]);

  useEffect(() => {
    if (props.examiners && props.isEdit) {
      setSelectedExaminer(cloneExaminer(props.examiners[0]));
    }
  }, [cloneExaminer, props.examiners, props.isEdit]);

  useEffect(() => {
    if (props.examiners.length && selectedExaminer) {
      setIsAddButtonDisable(JSON.stringify(props.examiners[0]) === JSON.stringify(selectedExaminer));
    }
  }, [selectedExaminer, props.examiners]);

  const sendDataToParent = useCallback(() => {
    setIsAddButtonDisable(true);
    if (selectedExaminer) {
      props.confirm([selectedExaminer]);
    } else props.confirm(examinerNumbers);
  }, [examinerNumbers, props, selectedExaminer]);

  const saveExaminerNumbers = (examinerNumber: string, userId: string) => {
    setExaminerNumbers((state) =>
      state.map((examiner) => {
        if (examiner.userId === userId) {
          return {
            ...examiner,
            examinerNumber: examinerNumber.trim(),
          };
        }
        return examiner;
      }),
    );
  };
  const setExaminerNumberByUserId = (userIdParam: string): string => {
    const foundExaminer: SelectedExaminer | undefined = examinerNumbers.find((e) => e.userId === userIdParam);
    return foundExaminer ? (foundExaminer.examinerNumber ? foundExaminer.examinerNumber : '') : '';
  };
  const isDisable = (examiner: SelectedExaminer): boolean => {
    if (!examiner.examinerNumber) {
      return true;
    }
    if (examiner.examinerNumber !== undefined) {
      return examiner.examinerNumber.length === 0;
    }
    return false;
  };
  useEffect(() => {
    if (!!props.isEdit) return;
    const hasUserWithoutNumber: SelectedExaminer[] = examinerNumbers.filter(isDisable);
    setIsAddButtonDisable(hasUserWithoutNumber.length > 0);
  }, [examinerNumbers, props.isEdit]);

  const handleExaminerNumberChange = (value: string) => {
    setSelectedExaminer((state) => {
      if (!state) return;
      return {
        ...state,
        examinerNumber: value,
      };
    });
  };

  const handleCancel = () => {
    setExaminerNumbers(props.examiners.map(cloneExaminer));
    if (props.isEdit && props.examiners.length > 0) {
      setSelectedExaminer(cloneExaminer(props.examiners[0]));
    }
    props.cancel();
  };

  return (
    <Dialog
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          handleCancel();
        }
      }}
      aria-label="Przypisz numer egzaminatora"
      open={open}
    >
      <Grid item xs={12}>
        <div className={classes.modalTitle}>
          {props.isEdit ? 'Edytuj numer egzaminatora' : 'Przypisz numer egzaminatora'}
        </div>
      </Grid>
      <div className={classes.formFields}>
        {props.examiners.map((examiner) => {
          const currentExaminer = props.isEdit
            ? selectedExaminer
            : examinerNumbers.find((current) => current.userId === examiner.userId);
          const isInputDisable = currentExaminer ? isDisable(currentExaminer) : true;
          return (
            <Fragment key={`${examiner.userId}-${examiner.firstName}`}>
              <div className={classes.subHeader}>
                {examiner.firstName} {examiner.lastName}
              </div>
              <TextField
                type={'text'}
                error={isInputDisable}
                helperText={isInputDisable ? 'Pole wymagane' : ''}
                className={classes.formInput}
                id="outlined"
                label="Numer egzaminatora*"
                variant="outlined"
                onChange={(e) => {
                  props.isEdit
                    ? handleExaminerNumberChange(e.target.value.trimStart())
                    : saveExaminerNumbers(e.target.value.trimStart(), examiner.userId);
                }}
                value={
                  props.isEdit ? selectedExaminer?.examinerNumber ?? '' : setExaminerNumberByUserId(examiner.userId)
                }
                inputProps={{
                  style: {
                    padding: '15.5px',
                    color: '#2C2D3E',
                  },
                }}
                InputLabelProps={{
                  style: { color: '#2C2D3E' },
                }}
                FormHelperTextProps={{
                  style: {
                    fontSize: '11px',
                    color: '#BC0000',
                  },
                }}
              />
            </Fragment>
          );
        })}
      </div>

      <Grid container style={{ marginTop: '10px', marginBottom: '22px' }}>
        <Grid item xs={12}>
          <CancelButton onClick={handleCancel}>Anuluj</CancelButton>
          <ConfirmButton
            disabled={isAddButtonDisable || (selectedExaminer && isDisable(selectedExaminer))}
            onClick={sendDataToParent}
          >
            Zapisz
          </ConfirmButton>
        </Grid>
      </Grid>
    </Dialog>
  );
}
